import React from 'react';
import { Card, Select, Input, Button, Badge, Popconfirm, Statistic, Typography, Space } from 'antd';
import { PlayCircleOutlined, StopOutlined } from '@ant-design/icons';
import type { ObserveWindow } from '../../../../services/devApi';
import { CHAIN_OPTIONS, formatDateTime } from '../../hooks/useFlowObserve';

const { Text } = Typography;

interface Props {
  obsWindow: ObserveWindow | null;
  isActive: boolean;
  auditCount: number;
  chainId: string;
  docPrefix: string;
  scenarioId: string;
  initializing: boolean;
  starting: boolean;
  stopping: boolean;
  onChainChange: (v: string) => void;
  onDocPrefixChange: (v: string) => void;
  onStart: () => void;
  onStop: () => void;
}

const ObserveControlCard: React.FC<Props> = ({
  obsWindow,
  isActive,
  auditCount,
  chainId,
  docPrefix,
  scenarioId,
  initializing,
  starting,
  stopping,
  onChainChange,
  onDocPrefixChange,
  onStart,
  onStop,
}) => {
  if (isActive && obsWindow) {
    return (
      <Card title="觀察窗控制" loading={initializing}>
        <div className="flex flex-wrap items-center gap-8">
          <Badge color="green" status="processing" text={<Text strong style={{ color: '#52c41a' }}>觀察中</Text>} />
          <Statistic title="即時寫入筆數" value={auditCount} valueStyle={{ fontSize: 22 }} />
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Chain</Text>
            <Text strong className="font-mono">{obsWindow.chainId}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>Scenario</Text>
            <Text className="font-mono">{obsWindow.scenarioId}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>單號前綴</Text>
            <Text strong className="font-mono">{obsWindow.docPrefix}</Text>
          </div>
          <div>
            <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>開始時間</Text>
            <Text>{formatDateTime(obsWindow.startedAt)}</Text>
          </div>
          <Popconfirm
            title="結束觀察"
            description="結束後將停止收集寫入紀錄並產生回執報告，確定嗎？"
            okText="結束"
            cancelText="取消"
            okButtonProps={{ danger: true }}
            onConfirm={onStop}
          >
            <Button danger type="primary" icon={<StopOutlined />} loading={stopping}>
              結束觀察
            </Button>
          </Popconfirm>
        </div>
        <div className="mt-3">
          <Text type="secondary">
            請用前綴 <Text code>{obsWindow.docPrefix}</Text> 建立交運單（例如 {obsWindow.docPrefix}A），所有資料庫寫入將即時顯示在下方。
          </Text>
        </div>
      </Card>
    );
  }

  return (
    <Card title="觀察窗控制" loading={initializing}>
      <div className="flex flex-wrap items-end gap-6">
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>業務鏈</Text>
          <Select
            value={chainId}
            onChange={onChainChange}
            options={CHAIN_OPTIONS}
            style={{ width: 240 }}
          />
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>測試單號前綴</Text>
          <Space direction="vertical" size={2}>
            <Input
              value={docPrefix}
              onChange={(e) => onDocPrefixChange(e.target.value)}
              style={{ width: 200 }}
              className="font-mono"
            />
            <Text type="secondary" style={{ fontSize: 12 }}>
              請用此前綴建立交運單，例如 {docPrefix || 'CH03L0611'}A
            </Text>
          </Space>
        </div>
        <div>
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>Scenario ID（自動生成）</Text>
          <Input value={scenarioId} readOnly disabled style={{ width: 260 }} className="font-mono" />
        </div>
        <Button
          type="primary"
          icon={<PlayCircleOutlined />}
          loading={starting}
          disabled={!docPrefix.trim()}
          onClick={onStart}
        >
          開始觀察
        </Button>
      </div>
    </Card>
  );
};

export default ObserveControlCard;
